/**
 * Soft percussion. Sky: bloom kick, shaker, airy clap. Dunes: frame drum / doumbek (dum, tek,
 * ka) and riq jingles. Kept round and low-level — they pulse under the music, never crack.
 */
import { SILENCE, perc } from '../env';
import { nyquist } from '../util';
import { link, noiseSrc, type Synth } from './synth';

export interface HitOpts {
  readonly pan?: number;
  readonly send?: number;
}

/** Pillowy kick: sine pitch-drop with a muffled contact tap. */
export function bloomKick(s: Synth, t: number, vel: number, o: HitOpts = {}): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.06 });
  const osc = v.osc('sine', 150);
  osc.frequency.setValueAtTime(150, t);
  osc.frequency.exponentialRampToValueAtTime(50, t + 0.11);
  const g = v.gain();
  link(osc, g, v.out);
  v.play(osc, t, perc(g.gain, t, 0.36 * vel, 0.003, 0.3));
  const n = noiseSrc(v, s.kit, s.kit.noise.pink, t, t + 0.05);
  const lp = v.filter('lowpass', 2200, 0.7);
  const ng = v.gain();
  link(n, lp, ng, v.out);
  perc(ng.gain, t, 0.05 * vel, 0.001, 0.014);
}

/** Shaker: high-passed noise grain. */
export function shaker(s: Synth, t: number, vel: number, o: HitOpts = {}): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.1 });
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, t + 0.12);
  const hp = v.filter('highpass', 5500, 0.7);
  const lp = v.filter('lowpass', Math.min(11000, nyquist(s.kit.ctx)), 0.5);
  const g = v.gain();
  link(n, hp, lp, g, v.out);
  perc(g.gain, t, 0.42 * vel, 0.008, 0.065);
}

/** Soft airy clap: three micro-bursts then a short breathy tail. */
export function softClap(s: Synth, t: number, vel: number, o: HitOpts = {}): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.3 });
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, t + 0.25);
  const bp = v.filter('bandpass', 1500, 0.9);
  const g = v.gain();
  link(n, bp, g, v.out);
  const pk = 0.9 * vel;
  const p = g.gain;
  p.setValueAtTime(0, t);
  for (let k = 0; k < 3; k++) {
    const tt = t + k * 0.011;
    p.linearRampToValueAtTime(pk, tt + 0.002);
    p.linearRampToValueAtTime(pk * 0.25, tt + 0.009);
  }
  p.linearRampToValueAtTime(pk * 0.8, t + 0.036);
  p.exponentialRampToValueAtTime(SILENCE, t + 0.036 + 0.14);
  p.linearRampToValueAtTime(0, t + 0.19);
}

/** Doumbek / bendir "dum": deep membrane thump with skin noise. */
export function dum(s: Synth, t: number, vel: number, o: HitOpts = {}): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.12 });
  const osc = v.osc('sine', 108);
  osc.frequency.setValueAtTime(108, t);
  osc.frequency.exponentialRampToValueAtTime(64, t + 0.09);
  const g = v.gain();
  link(osc, g, v.out);
  v.play(osc, t, perc(g.gain, t, 0.4 * vel, 0.002, 0.42));
  const n = noiseSrc(v, s.kit, s.kit.noise.brown, t, t + 0.1);
  const lp = v.filter('lowpass', 420, 0.8);
  const ng = v.gain();
  link(n, lp, ng, v.out);
  perc(ng.gain, t, 0.18 * vel, 0.002, 0.07);
}

/** Doumbek "tek": rim crack (band-passed noise) with a short ring. */
export function tek(s: Synth, t: number, vel: number, o: HitOpts = {}): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.14 });
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, t + 0.08);
  const bp = v.filter('bandpass', 3300, 1.6);
  const g = v.gain();
  link(n, bp, g, v.out);
  perc(g.gain, t, 1.1 * vel, 0.0008, 0.055);
  const osc = v.osc('sine', 740);
  const og = v.gain();
  link(osc, og, v.out);
  v.play(osc, t, perc(og.gain, t, 0.07 * vel, 0.001, 0.05));
}

/** Doumbek "ka": the softer off-hand tek. */
export function ka(s: Synth, t: number, vel: number, o: HitOpts = {}): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.12 });
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, t + 0.06);
  const bp = v.filter('bandpass', 2500, 1.3);
  const g = v.gain();
  link(n, bp, g, v.out);
  perc(g.gain, t, 0.8 * vel, 0.0008, 0.04);
}

/** Riq: tambourine jingles — two narrow metallic resonances over high-passed noise. */
export function riq(s: Synth, t: number, vel: number, o: HitOpts = {}): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.2 });
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, t + 0.2);
  const hp = v.filter('highpass', 5200, 0.7);
  const g = v.gain();
  link(n, hp);
  const ny = nyquist(s.kit.ctx);
  for (const [hz, amt] of [
    [6400, 1],
    [8600, 0.8],
  ] as const) {
    const bp = v.filter('bandpass', Math.min(hz, ny), 9);
    const bg = v.gain(amt * 2.2);
    link(hp, bp, bg, g);
  }
  const direct = v.gain(0.35);
  link(hp, direct, g);
  g.connect(v.out);
  perc(g.gain, t, 0.6 * vel, 0.001, 0.14);
}
